<template>
  <base-sheet :visible="visible" @close="$emit('close')">
  <h3 class="sheet-title">Diagnóstico de downloads</h3>

  <p v-if="!entries.length" class="empty">
    Nenhum download registrado ainda.
  </p>

  <template v-else>
    <div v-if="hasStats" class="stats">
      <div
        v-for="(stat, format) in stats"
        :key="format"
        class="stat-block"
      >
        <span class="stat-format">{{ formatLabel(format) }}</span>
        <span v-if="stat.count > 0" class="stat-line">
          <span>{{ stat.count }} {{ stat.count === 1 ? 'download' : 'downloads' }}</span>
          ·
          <span>{{ formatDuration(stat.avgWallclockMs) }} médio</span>
          ·
          <span>{{ formatThroughput(stat.avgThroughputBps) }}</span>
        </span>
        <span v-else class="stat-line stat-line--empty">
          Sem downloads reais — só reaproveitamentos do cache.
        </span>
        <span v-if="stat.avgTtfbMs" class="stat-sub">
          TTFB médio: {{ formatDuration(stat.avgTtfbMs) }}
        </span>
        <span v-if="stat.reusedCount > 0" class="stat-sub stat-sub--reused">
          +{{ stat.reusedCount }} {{ stat.reusedCount === 1 ? 'reaproveitamento' : 'reaproveitamentos' }} do cache
        </span>
      </div>
    </div>

    <h4 class="list-title">Últimos {{ visibleEntries.length }}</h4>
    <div class="list">
      <div
        v-for="entry in visibleEntries"
        :key="entry.id"
        class="entry"
        :class="entryClass(entry)"
      >
        <div class="entry-line">
          <span class="entry-format">{{ formatLabel(entry.format) }}</span>
          <span class="entry-quality">{{ qualityLabel(entry) }}</span>
          <span v-if="entry.reusedFromCache" class="entry-tag" title="Sessão concluída a partir do cache local — sem download real">cache</span>
          <span class="entry-duration">{{ formatDuration(entry.wallclock) }}</span>
        </div>
        <div class="entry-meta">
          <span v-if="entry.error" class="meta-err">{{ entry.error }}</span>
          <span v-else-if="entry.cancelled" class="meta-err">Cancelado</span>
          <span v-else-if="entry.reusedFromCache" class="meta-muted">
            Já estava em disco · {{ formatBytes(entry.actualBytes) }}
            <span v-if="entry.segmentCount"> · {{ entry.segmentCount }} segmentos</span>
          </span>
          <template v-else>
            <span v-if="entry.actualBytes">{{ formatBytes(entry.actualBytes) }}</span>
            <span v-if="entry.throughputBps"> · {{ formatThroughput(entry.throughputBps) }}</span>
            <span v-if="entry.ttfb !== null"> · TTFB {{ formatDuration(entry.ttfb) }}</span>
            <span v-if="entry.segmentCount"> · {{ entry.segmentCount }} segmentos</span>
          </template>
        </div>
        <div class="entry-sub">
          <span>{{ entry.loId }}</span>
          <span v-if="entry.connectionType"> · {{ entry.connectionType }}</span>
          <span v-if="entry.variantHeight"> · {{ entry.variantHeight }}p</span>
          <span> · {{ relativeTime(entry.startedAt) }}</span>
        </div>
      </div>
    </div>
  </template>

  <div class="actions">
    <button
      type="button"
      class="action-btn secondary"
      :disabled="!entries.length"
      @click="onCopy"
    >
      {{ copyLabel }}
    </button>
    <button
      type="button"
      class="action-btn destructive"
      :disabled="!entries.length"
      @click="onClear"
    >
      Limpar
    </button>
  </div>
  <button type="button" class="action-btn cancel" @click="$emit('close')">Fechar</button>
  </base-sheet>
</template>

<script>
import BaseSheet from '@/components/base/BaseSheet';

import { getAll, getStats, clear, formatDuration, formatThroughput } from '@/services/download/downloadTelemetry';
import { formatBytes } from '@/services/download/downloadHelpers';

// Bottom sheet de diagnóstico de downloads. Mostra:
//   - Médias agregadas por formato (HLS / etc), só sessões completed
//   - Lista dos últimos N downloads, mais recentes primeiro
//   - Botão "Copiar JSON" → coloca o array completo no clipboard pra análise
//     externa (planilha, ferramentas)
//   - Botão "Limpar" → reset do histórico
//
// Não polui state global — lê telemetry on-demand em data() e em refresh ao
// abrir (watch visible). Sem reatividade automática: pra valer dados novos,
// fechar e abrir a sheet.
const FORMAT_LABELS = { hls: 'HLS', unknown: '—' };
const QUALITY_LABELS = { low: 'Baixa', medium: 'Média', high: 'Alta' };
const MAX_VISIBLE = 20;

export default {
  name: 'DownloadDiagnosticsSheet',
  components: { BaseSheet },
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      entries: [],
      stats: {},
      copyLabel: 'Copiar JSON'
    };
  },
  computed: {
    visibleEntries() {
      return this.entries.slice(0, MAX_VISIBLE);
    },
    hasStats() {
      return Object.keys(this.stats).length > 0;
    }
  },
  watch: {
    visible(now) {
      if (now) this.refresh();
    }
  },
  methods: {
    refresh() {
      this.entries = getAll();
      this.stats = getStats();
    },
    formatDuration,
    formatThroughput,
    formatBytes,
    formatLabel(format) { return FORMAT_LABELS[format] || format; },
    qualityLabel(entry) {
      if (!entry.quality) return '';
      return QUALITY_LABELS[entry.quality] || '';
    },
    entryClass(entry) {
      if (entry.error) return 'failed';
      if (entry.cancelled) return 'cancelled';
      if (entry.reusedFromCache) return 'reused';
      return 'ok';
    },
    // Formato amigável "há 3 min", "há 2 h", "há 5 d". Suficiente pra
    // diagnóstico — não vale a pena puxar uma lib só pra isso.
    relativeTime(ts) {
      if (!ts) return '—';
      const diff = Date.now() - ts;
      const sec = Math.floor(diff / 1000);
      if (sec < 60) return 'há instantes';
      const min = Math.floor(sec / 60);
      if (min < 60) return `há ${min} min`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `há ${hr} h`;
      const day = Math.floor(hr / 24);
      return `há ${day} d`;
    },
    async onCopy() {
      try {
        await navigator.clipboard.writeText(JSON.stringify(this.entries, null, 2));
        this.copyLabel = 'Copiado!';
        setTimeout(() => { this.copyLabel = 'Copiar JSON'; }, 1500);
      } catch (e) {
        // Capacitor WebView às vezes não tem clipboard API moderna; usa fallback
        // via textarea+execCommand.
        try {
          const ta = document.createElement('textarea');
          ta.value = JSON.stringify(this.entries, null, 2);
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          this.copyLabel = 'Copiado!';
          setTimeout(() => { this.copyLabel = 'Copiar JSON'; }, 1500);
        } catch {
          this.copyLabel = 'Falhou';
          setTimeout(() => { this.copyLabel = 'Copiar JSON'; }, 1500);
        }
      }
    },
    onClear() {
      clear();
      this.refresh();
    }
  }
};
</script>

<style scoped lang="scss">



.sheet-title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--md-text, #fff);
}

.empty {
  margin: 24px 0;
  text-align: center;
  font-size: 13px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.55));
}

.stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
}

.stat-block {
  padding: 10px 12px;
  background: var(--md-surface-elev, rgba(255, 255, 255, 0.04));
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-format {
  font-size: 13px;
  font-weight: 600;
  color: var(--md-text, #fff);
}

.stat-line {
  font-size: 12px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.7));
}

.stat-sub {
  font-size: 11px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.5));
}

.list-title {
  margin: 8px 0 4px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.55));
}

.list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.entry {
  padding: 10px 12px;
  background: var(--md-surface-elev, rgba(255, 255, 255, 0.04));
  border-radius: 10px;
  border-left: 3px solid transparent;

  &.failed { border-left-color: #ef4444; }
  &.cancelled { border-left-color: #f59e0b; }
  &.ok { border-left-color: rgba(34, 197, 94, 0.5); }
  &.reused { border-left-color: rgba(96, 165, 250, 0.6); }
}

.entry-line {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  color: var(--md-text, #fff);
}

.entry-format {
  font-weight: 700;
}

.entry-quality {
  font-size: 11px;
  color: var(--md-text-dim);
  padding: 2px 6px;
  background: var(--md-surface, rgba(255, 255, 255, 0.05));
  border-radius: 4px;
}

// Badge "cache" exibido em entries de reaproveitamento. Mesma forma do
// entry-quality mas com tom azulado, alinhado com a borda esquerda do entry
// na classe `.reused`.
.entry-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  background: rgba(96, 165, 250, 0.15);
  color: #60a5fa;
  border-radius: 4px;
}

.entry-duration {
  margin-left: auto;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.entry-meta {
  margin-top: 4px;
  font-size: 12px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.6));
  font-variant-numeric: tabular-nums;
}

.entry-sub {
  margin-top: 2px;
  font-size: 11px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.45));
  font-variant-numeric: tabular-nums;
}

.meta-err {
  color: #ef4444;
}

.meta-muted {
  color: var(--md-text-dim, rgba(255, 255, 255, 0.5));
  font-style: italic;
}

.stat-line--empty {
  color: var(--md-text-dim, rgba(255, 255, 255, 0.5));
  font-style: italic;
}

.stat-sub--reused {
  color: #60a5fa;
}

.actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.secondary {
    flex: 1;
    background: var(--md-surface-elev, rgba(255, 255, 255, 0.06));
    color: var(--md-text, #fff);
  }

  &.destructive {
    flex: 1;
    background: transparent;
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.4);
  }

  &.cancel {
    margin-top: 6px;
    padding: 14px;
    background: var(--md-surface-elev, rgba(255, 255, 255, 0.05));
    color: var(--md-text-dim, rgba(255, 255, 255, 0.7));
    font-size: 14px;
  }
}

</style>
